import cloneDeep from 'lodash/cloneDeep';
import {
  FilterOperatorTypes,
  JoinPrefix,
  LogicalExprTypes,
  ExpressionTypes,
} from 'core/constants/report-expressions';
import { JoinItem } from 'core/models/report-builder/report-builder';
import {
  IExpression,
  IComparisonExprPart,
  IColumnExprPart,
  newColumnExpr,
  newComparisonExpr,
  newAndExpr,
  newOrExpr,
} from 'core/models/report-expressions';
import { DataSourceSelectionType, ColumnDataTypes } from 'core/constants/report';
import { NumericDataTypes, DateDataTypes } from 'core/constants/dashboard';
import { Operators } from 'core/constants/report-builder';
import { isValidArrayAndNotEmpty } from 'components/feature/Report/ReportSidebar/common/helpers';
import { normalizeJoinConditions, extractJoinConditions } from './data-source.util';

const getColumnPart = (expr: IExpression<ExpressionTypes.Column> | null | undefined): IColumnExprPart | undefined => {
  if (!expr || expr.Type !== ExpressionTypes.Column) return undefined;
  return expr.Part as IColumnExprPart;
};

export const getConditionsFromJoin = (joinData: JoinItem) => extractJoinConditions(normalizeJoinConditions(joinData?.On));

const createLogicalExpr = (conditions: IExpression[], logicalOp: LogicalExprTypes): IExpression => (logicalOp === LogicalExprTypes.And ? newAndExpr(conditions) : newOrExpr(conditions));

const getTableNameWithPrefix = (table: string, primaryDataSource: string): string => (table === primaryDataSource ? table : `${JoinPrefix}${table}`);

export const getDefaultFieldFromJoinCondition = (joinData: JoinItem, side: DataSourceSelectionType, condIdx?: number): string => {
  if (!joinData?.On) return '';
  const { conditions } = getConditionsFromJoin(joinData);
  if (!isValidArrayAndNotEmpty(conditions)) return '';
  const idx = (condIdx ?? 0);
  if (idx < 0 || idx >= conditions.length) return '';
  const part = conditions[idx]?.Part as IComparisonExprPart<FilterOperatorTypes> | undefined;
  if (!part) return '';
  if (side === DataSourceSelectionType.lhsField && part.LHS) {
    return getColumnPart(part.LHS as IExpression<ExpressionTypes.Column>)?.Column?.SchemaName || '';
  }
  if (side === DataSourceSelectionType.rhsField && part.RHS) {
    return getColumnPart(part.RHS as IExpression<ExpressionTypes.Column>)?.Column?.SchemaName || '';
  }
  return '';
};

/**
 * Add new join condition to join block
 */
export const addJoinCondition = (joinData: JoinItem, logicalOp: LogicalExprTypes, afterIdx: number, primaryDataSource: string, joinConditionsLimit: number): { updatedJoinData: JoinItem | null; newConditionIndex: number | null } => {
  if (!joinData || !primaryDataSource || joinConditionsLimit <= 0) return { updatedJoinData: null, newConditionIndex: null };
  const clonedJoinData = cloneDeep(joinData);
  const { conditions } = getConditionsFromJoin(clonedJoinData);
  if (conditions.length >= joinConditionsLimit || afterIdx < -1 || afterIdx >= conditions.length) return { updatedJoinData: null, newConditionIndex: null };
  // Determine LHS table: use first condition's LHS table if available, otherwise fall back to Primary or primaryDataSource
  let primaryTable = clonedJoinData.Primary || primaryDataSource;
  if (isValidArrayAndNotEmpty(conditions)) {
    const firstPart = conditions[0]?.Part as IComparisonExprPart<FilterOperatorTypes>;
    if (firstPart?.LHS) {
      const lhsPart = getColumnPart(firstPart.LHS as IExpression<ExpressionTypes.Column>);
      if (lhsPart?.TableNameOverride) {
        primaryTable = lhsPart.TableNameOverride.replace(JoinPrefix, '') || primaryTable;
      }
    }
  }
  if (!primaryTable || !clonedJoinData.With) return { updatedJoinData: null, newConditionIndex: null };
  const newCondition = newComparisonExpr(
    newColumnExpr('', '', ColumnDataTypes.String, getTableNameWithPrefix(primaryTable, primaryDataSource)),
    newColumnExpr('', '', ColumnDataTypes.String, `${JoinPrefix}${clonedJoinData.With}`),
    FilterOperatorTypes.EQ,
  );
  const updatedConditions = [...conditions.slice(0, afterIdx + 1), newCondition, ...conditions.slice(afterIdx + 1)];
  clonedJoinData.On = createLogicalExpr(updatedConditions, logicalOp);
  return { updatedJoinData: clonedJoinData, newConditionIndex: afterIdx + 1 };
};

/**
 * Remove join condition from join block
 */
export const removeJoinCondition = (joinData: JoinItem, condIdx: number, currentConditionIndex: number): { updatedJoinData: JoinItem | null; adjustedConditionIndex: number | null } => {
  if (!joinData || condIdx < 0) return { updatedJoinData: null, adjustedConditionIndex: null };
  const clonedJoinData = cloneDeep(joinData);
  const { conditions, logicalOp } = getConditionsFromJoin(clonedJoinData);
  if (conditions.length <= 1 || condIdx >= conditions.length) return { updatedJoinData: null, adjustedConditionIndex: null };
  const updatedConditions = [...conditions.slice(0, condIdx), ...conditions.slice(condIdx + 1)];
  clonedJoinData.On = createLogicalExpr(updatedConditions, logicalOp);
  const adjustedConditionIndex = currentConditionIndex >= updatedConditions.length ? Math.max(0, updatedConditions.length - 1) : currentConditionIndex;
  return { updatedJoinData: clonedJoinData, adjustedConditionIndex };
};

/**
 * Update logical operator (AND/OR) for all conditions
 */
export const updateLogicalOperator = (joinData: JoinItem, logicalOp: LogicalExprTypes): JoinItem | null => {
  if (!joinData) return null;
  const clonedJoinData = cloneDeep(joinData);
  const { conditions } = getConditionsFromJoin(clonedJoinData);
  if (conditions.length === 0) return clonedJoinData;
  clonedJoinData.On = createLogicalExpr(conditions, logicalOp);
  return clonedJoinData;
};

const updateConditionInExpression = (conditions: IExpression[], condIdx: number, updatedCondition: IExpression, logicalOp: LogicalExprTypes): IExpression => {
  const updatedConditions = [...conditions];
  updatedConditions[condIdx] = updatedCondition;
  return createLogicalExpr(updatedConditions, logicalOp);
};

/**
 * Extracts column expression parts from a comparison expression part
 * @param part - The comparison expression part containing LHS and RHS
 * @returns Object containing lhsPart and rhsPart column expressions, or undefined if invalid
 */
export const extractColumnPartsFromCondition = (part: IComparisonExprPart<FilterOperatorTypes>): { lhsPart: IColumnExprPart | undefined; rhsPart: IColumnExprPart | undefined } => ({
  lhsPart: getColumnPart(part?.LHS as IExpression<ExpressionTypes.Column> | null | undefined),
  rhsPart: getColumnPart(part?.RHS as IExpression<ExpressionTypes.Column> | null | undefined),
});

/**
 * Update comparison operator for specific join condition
 */
export const updateConditionOperator = (joinData: JoinItem, condIdx: number, operator: FilterOperatorTypes): JoinItem | null => {
  if (!joinData || condIdx < 0 || !operator) return null;
  const clonedJoinData = cloneDeep(joinData);
  const { conditions, logicalOp } = getConditionsFromJoin(clonedJoinData);
  if (condIdx >= conditions.length || conditions.length === 0) return null;
  const part = conditions[condIdx]?.Part as IComparisonExprPart<FilterOperatorTypes> | undefined;
  if (!part?.LHS || !part?.RHS) return null;
  clonedJoinData.On = updateConditionInExpression(conditions, condIdx, newComparisonExpr(part.LHS, part.RHS, operator), logicalOp);
  return clonedJoinData;
};

/**
 * Get valid operators for LHS and RHS data types
 * Comparison operators (GT, LT, GTE, LTE) only valid when both sides are numeric or both date/time
 * Equality operators (EQ, NOT_EQ) always valid
 */
export const getValidOperatorsForDataTypes = (lhsDataType: string | undefined, rhsDataType: string | undefined): FilterOperatorTypes[] => {
  const baseOperators = [FilterOperatorTypes.EQ, FilterOperatorTypes.NOT_EQ];
  if (!lhsDataType || !rhsDataType) return [...baseOperators, FilterOperatorTypes.GT, FilterOperatorTypes.LT, FilterOperatorTypes.GTE, FilterOperatorTypes.LTE];
  const bothNumeric = NumericDataTypes.includes(lhsDataType) && NumericDataTypes.includes(rhsDataType);
  const bothDateTime = DateDataTypes.includes(lhsDataType) && DateDataTypes.includes(rhsDataType);
  return bothNumeric || bothDateTime ? [...baseOperators, FilterOperatorTypes.GT, FilterOperatorTypes.LT, FilterOperatorTypes.GTE, FilterOperatorTypes.LTE] : baseOperators;
};

/**
 * Get valid operator (returns EQ if current operator invalid)
 */
export const getValidOperatorForDataTypes = (operator: FilterOperatorTypes, lhsDataType: string | undefined, rhsDataType: string | undefined): FilterOperatorTypes => (getValidOperatorsForDataTypes(lhsDataType, rhsDataType).includes(operator) ? operator : FilterOperatorTypes.EQ);

// Operator mapping for label ↔ enum conversions
const OPERATOR_MAP = {
  [Operators.EQUAL_TO]: {
    filter: FilterOperatorTypes.EQ,
    builder: Operators.EQUAL_TO,
  },
  [Operators.NOT_EQUAL_TO]: {
    filter: FilterOperatorTypes.NOT_EQ,
    builder: Operators.NOT_EQUAL_TO,
  },
  [Operators.GREATER_THAN]: {
    filter: FilterOperatorTypes.GT,
    builder: Operators.GREATER_THAN,
  },
  [Operators.LESS_THAN]: {
    filter: FilterOperatorTypes.LT,
    builder: Operators.LESS_THAN,
  },
  [Operators.GREATER_THAN_OR_EQUAL_TO]: {
    filter: FilterOperatorTypes.GTE,
    builder: Operators.GREATER_THAN_OR_EQUAL_TO,
  },
  [Operators.LESS_THAN_OR_EQUAL_TO]: {
    filter: FilterOperatorTypes.LTE,
    builder: Operators.LESS_THAN_OR_EQUAL_TO,
  },
};

const DEFAULT_OPERATOR = {
  filter: FilterOperatorTypes.EQ,
  builder: Operators.EQUAL_TO,
  label: Operators.EQUAL_TO,
};

export const getOperatorType = (label: string): Operators => OPERATOR_MAP[label as keyof typeof OPERATOR_MAP]?.builder ?? DEFAULT_OPERATOR.builder;

export const getFilterOperatorType = (label: string): FilterOperatorTypes => OPERATOR_MAP[label as keyof typeof OPERATOR_MAP]?.filter ?? DEFAULT_OPERATOR.filter;

export const getOperatorLabel = (operator: FilterOperatorTypes): string => Object.entries(OPERATOR_MAP).find(([_, v]) => v.filter === operator)?.[0] ?? DEFAULT_OPERATOR.label;
